import React, { useState, useEffect } from 'react';
import { CouponService } from '../services/couponService';
import { ApiService } from '../services/api_service';

const TicketIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronIcon = ({ down }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: down ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Spinner = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: 'spin 0.8s linear infinite' }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const CouponInput = ({
  onCouponApplied,
  onCouponRemoved,
  onLoginRequired,
  appliedCoupon,
  subtotal,
  userId,
  eventId,
  selectedTicketIds = [],
  selectedTicketsData = [],
  disabled = false
}) => {
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [isApplying, setIsApplying] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isExpanded && availableCoupons.length === 0) {
      fetchAvailableCoupons();
    }
  }, [isExpanded]);

  const filterCoupon = (coupon) => {
    if (coupon.status !== 'ACTIVE') return false;
    const now = new Date();
    if (coupon.validFrom && now < new Date(coupon.validFrom)) return false;
    if (coupon.validUntil && now > new Date(coupon.validUntil)) return false;
    if (coupon.restrictedToUserIds?.length && !coupon.restrictedToUserIds.includes(userId)) return false;
    if (coupon.restrictedToTicketTypeIds?.length &&
      !selectedTicketIds.some(id => coupon.restrictedToTicketTypeIds.includes(id))) return false;
    if (coupon.limits?.maxTotalRedemptions > 0 &&
      coupon.limits.totalRedeemedCount >= coupon.limits.maxTotalRedemptions) return false;
    if (subtotal < (coupon.minOrderAmount || 0)) return false;
    return true;
  };

  const fetchAvailableCoupons = async () => {
    setIsLoadingCoupons(true);
    setError('');
    try {
      const response = await ApiService.getEventCoupons(eventId, userId);
      const allCoupons = response.data || [];
      setAvailableCoupons(Array.isArray(allCoupons) ? allCoupons.filter(filterCoupon) : []);
    } catch (err) {
      setError('Failed to load coupons');
    } finally {
      setIsLoadingCoupons(false);
    }
  };

  const formatLabel = (coupon) => {
    if (coupon.discountType === 'PERCENTAGE') {
      const cap = coupon.maxDiscountCap ? ` upto ₹${coupon.maxDiscountCap}` : '';
      return `${coupon.discountValue}% OFF${cap}`;
    }
    if (coupon.discountType === 'FLAT') return `FLAT ₹${coupon.discountValue} OFF`;
    if (coupon.discountType === 'BOGO') return `BUY ${coupon.bogoConfig?.buyQty} GET ${coupon.bogoConfig?.getQty} FREE`;
    return `₹${coupon.discountValue} OFF`;
  };

  const typeBadgeStyle = (type) => {
    if (type === 'BOGO') return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
    if (type === 'FLAT') return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
    return 'bg-[#17BD3B]/20 text-[#17BD3B] border border-[#17BD3B]/30';
  };

  const handleApplyCoupon = async (coupon) => {
    setIsApplying(coupon.id);
    setError('');
    try {
      const response = await ApiService.validateCoupon({
        coupon_code: coupon.code,
        user_id: userId,
        event_id: eventId,
        tickets: selectedTicketsData,
      });
      const result = response.data || response;
      if (result.success === false) {
        const msg = typeof result.message === 'object' ? result.message?.message : result.message;
        setError(msg || 'Coupon is not valid');
        return;
      }
      const breakdown = result.data?.breakdown || result.breakdown;
      onCouponApplied({
        couponCode: coupon.code,
        couponId: coupon.id,
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
        bogoConfig: coupon.bogoConfig,
        discountAmount: breakdown?.discount_applied ?? 0,
        originalSubtotal: subtotal,
        newSubtotal: breakdown?.final_amount ?? subtotal,
      });
      setIsExpanded(false);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsApplying(null);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponRemoved();
    setError('');
  };

  const toggleExpanded = () => {
    if (disabled) return;
    if (!userId) { onLoginRequired?.(); return; }
    setIsExpanded(v => !v);
    setError('');
  };

  return (
    <div className="mb-4">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeSlide { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* ── Applied banner ── */}
      {appliedCoupon ? (
        <div className="rounded-xl border border-[#17BD3B]/50 bg-[#17BD3B]/10 p-3 flex items-center justify-between gap-3"
          style={{ animation: 'fadeSlide 0.25s ease' }}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#17BD3B] flex items-center justify-center text-white">
              <CheckIcon />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold tracking-widest text-[#17BD3B] bg-[#17BD3B]/15 border border-[#17BD3B]/30 px-2 py-0.5 rounded-md font-mono">
                  {appliedCoupon.couponCode}
                </span>
                <span className="text-[13px] font-semibold text-white">
                  {CouponService.formatCouponDisplay(appliedCoupon)}
                </span>
              </div>
              <p className="text-[12px] text-[#17BD3B] mt-0.5 font-medium">
                You saved ₹{appliedCoupon.discountAmount.toFixed(2)}
              </p>
            </div>
          </div>
          <button
            onClick={handleRemoveCoupon}
            disabled={disabled}
            className="flex-shrink-0 text-white/40 hover:text-white/80 transition-colors p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-40"
            title="Remove coupon"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      ) : (
        <>
          {/* ── Toggle row ── */}
          <button
            onClick={toggleExpanded}
            disabled={disabled}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#202020] border border-[#333] hover:border-[#444] hover:bg-[#252525] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2.5 text-white/80">
              <span className="text-[#17BD3B]"><TicketIcon /></span>
              <span className="text-[14px] font-medium">Apply Coupon</span>
              {availableCoupons.length > 0 && !isExpanded && (
                <span className="text-[11px] font-semibold bg-[#17BD3B]/20 text-[#17BD3B] px-2 py-0.5 rounded-full border border-[#17BD3B]/30">
                  {availableCoupons.length} offer{availableCoupons.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <span className="text-white/40"><ChevronIcon down={isExpanded} /></span>
          </button>

          {/* ── Dropdown ── */}
          {isExpanded && (
            <div className="mt-2 rounded-xl border border-[#333] bg-[#191919] overflow-hidden"
              style={{ animation: 'fadeSlide 0.2s ease' }}>

              {isLoadingCoupons ? (
                <div className="flex items-center gap-3 px-4 py-5 text-white/50 text-sm">
                  <Spinner size={18} />
                  <span>Finding offers for you...</span>
                </div>
              ) : availableCoupons.length > 0 ? (
                <div className="divide-y divide-[#2a2a2a]">
                  {availableCoupons.map((coupon) => (
                    <div key={coupon.id} className="flex items-center gap-3 px-4 py-3.5 hover:bg-[#202020] transition-colors">
                      {/* Left accent */}
                      <div className="flex-shrink-0 w-1 self-stretch rounded-full bg-[#17BD3B]/60" />

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[11px] font-bold tracking-widest text-white/90 bg-white/8 border border-white/15 px-2 py-0.5 rounded-md font-mono">
                            {coupon.code}
                          </span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${typeBadgeStyle(coupon.discountType)}`}>
                            {formatLabel(coupon)}
                          </span>
                        </div>
                        {coupon.description && (
                          <p className="text-[12px] text-white/50 leading-snug line-clamp-2">{coupon.description}</p>
                        )}
                        {coupon.minOrderAmount > 0 && (
                          <p className="text-[11px] text-white/35 mt-0.5">Min. order ₹{coupon.minOrderAmount}</p>
                        )}
                      </div>

                      {/* Apply btn */}
                      <button
                        onClick={() => handleApplyCoupon(coupon)}
                        disabled={disabled || isApplying !== null}
                        className="flex-shrink-0 text-[13px] font-semibold text-[#17BD3B] border border-[#17BD3B]/50 px-3 py-1.5 rounded-lg hover:bg-[#17BD3B]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[56px] flex items-center justify-center"
                      >
                        {isApplying === coupon.id ? <Spinner size={14} /> : 'Apply'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 px-4 py-8 text-white/30">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                    <line x1="9" y1="12" x2="15" y2="12" strokeDasharray="2 2" />
                  </svg>
                  <span className="text-[13px]">No coupons available right now</span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 mx-4 mb-3 mt-1 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-400 text-[12px]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CouponInput;
